import { createStyles, makeStyles } from '@material-ui/core';
import { Theme } from '@mui/material';
import { Edge, Node } from '@xyflow/react';
import ELK from 'elkjs/lib/elk.bundled.js';

export const useFlowLayoutStyles = makeStyles((theme: Theme) =>
  createStyles({
    nodeWrapper: {
      minHeight: '55px',
      border: '1px solid',
      borderColor: 'rgba(0, 0, 0, 0.25)',
      borderRadius: '4px',
      backgroundColor: theme.palette.background.default,
      boxShadow: theme.shadows[8],
      '&:hover': {
        borderColor: theme.palette.secondary.main,
      },
      '&.selectable': {
        cursor: 'pointer'
      },
      '&.selected': {
        borderColor: theme.palette.secondary.main,
        boxShadow: `
          0px 7px 8px -4px ${theme.palette.secondary.main}33, 
          0px 12px 17px 2px ${theme.palette.secondary.main}24,
          0px 5px 22px 4px ${theme.palette.secondary.main}1f
        `
      }
    },
    rootNodeWrapper: {
      color: '#fbfbfb',
      backgroundColor: '#3b3b3b',
      '&:hover': {
        borderColor: '#fbfbfbb3',
      },
      '&.selected': {
        borderColor: '#ffffffb3'
      },
      '& p': {
        color: '#fbfbfb90'
      },
      '& svg': {
        fill: '#fbfbfb'
      }
    },
    edgeWrapper: {
      '& .react-flow__edge-textbg': {
        fill: theme.palette.background.paper
      },
      '&.selected .react-flow__edge-text': {
        fill: theme.palette.secondary.main,
        fontWeight: 700
      }
    }
  })
);

const elk = new ELK();

export const layoutWithELK = async (nodes: any, edges: any, classes: { rootNode: string, node: string, edge: string } = { rootNode: '', node: '', edge: '' })
  : (Promise<{ nodes: Node[], edges: Edge[] }>) => {

  // Elk has a *huge* amount of options to configure. To see everything you can
  // tweak check out:
  //
  // - https://www.eclipse.org/elk/reference/algorithms.html
  // - https://www.eclipse.org/elk/reference/options.html
  const elkOptions = {
    'elk.algorithm': 'layered',
    'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
    'elk.layered.spacing.nodeNodeBetweenLayers': '125',
    'elk.spacing.nodeNode': '100',
  };

  const graph = {
    id: 'root',
    layoutOptions: { ...elkOptions },
    children: nodes.map((node: any) => {

      const isRootNode = (node.data.scope === 'internal' && node.data.type === 'exchange');
      const className = `${classes.node} ${isRootNode ? classes.rootNode : ''}`;

      return ({
        ...node,
        ...{ position: { x: 0, y: 0 }, className },
        width: 250,
        height: 50
      });
    }),
    edges: edges.map((edge: any) => ({ ...edge, ...{ className: classes.edge } }))
  };

  const layoutedGraph = await elk.layout(graph);

  return ({
    nodes: layoutedGraph.children?.map((node) => ({
      ...node, position: { x: node.x, y: node.y },
    })) as Node[] || [],
    edges: layoutedGraph.edges as any as Edge[] || [],
  });
};